import { useMemo } from 'react';
import dayjs from 'dayjs';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { VersionDrawer, type VersionEntry } from '@shared/components/VersionDrawer';
import { campaignsApi } from '@api/campaigns';
import { resolveApiErrorMessage } from '@/i18n/apiError';
import { recordAuditAction } from '@features/audit/recordAction';

interface Props {
  open: boolean;
  campaignId: string;
  currentBody: string;
  onClose: () => void;
}

export function CampaignVersionsDrawer({ open, campaignId, currentBody, onClose }: Props) {
  const { t } = useTranslation(['campaigns', 'common', 'validation', 'errors']);
  const qc = useQueryClient();

  const { data } = useQuery({
    queryKey: ['campaign-versions', campaignId],
    queryFn: () => campaignsApi.listVersions(campaignId),
    enabled: open,
  });

  const versions = useMemo<VersionEntry[]>(() => {
    const list = data ?? [];
    return [...list]
      .sort((a, b) => b.versionNumber - a.versionNumber)
      .map((v, idx) => ({
        id: String(v.versionNumber),
        label: t('versions.label', {
          version: v.versionNumber,
          note: v.changeNote ?? t('versions.defaultNote'),
        }),
        author: v.authorEmail ?? t('versions.systemAuthor'),
        createdAt: dayjs(v.createdAt).format('DD.MM.YYYY HH:mm'),
        snapshot: v.body,
        current: idx === 0,
      }));
  }, [data, t]);

  const restoreMutation = useMutation({
    mutationFn: (versionNumber: number) => campaignsApi.restoreVersion(campaignId, versionNumber),
    onSuccess: async (_d, versionNumber) => {
      toast.success(t('toast.versionRestored', { version: versionNumber }));
      await qc.invalidateQueries({ queryKey: ['campaigns', campaignId] });
      await qc.invalidateQueries({ queryKey: ['campaign-versions', campaignId] });
      await qc.invalidateQueries({ queryKey: ['campaigns'] });
      void recordAuditAction({
        action: 'campaign.version.restore',
        resource: 'campaign',
        resourceId: campaignId,
        summary: t('audit.versionRestore', { version: versionNumber }),
        meta: { versionNumber },
      });
      onClose();
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  return (
    <VersionDrawer
      open={open}
      onClose={onClose}
      versions={versions}
      currentContent={currentBody}
      onRestore={async (v) => {
        const num = Number(v.id);
        if (Number.isFinite(num)) await restoreMutation.mutateAsync(num);
      }}
    />
  );
}
